/*
  vz-recs.js — "Fits your car" cross-sell strips on the product page.

  Flow: read the shopper's saved vehicle -> work out which categories complement
  the part being viewed -> ask the storefront for each of those collections with
  the vehicle filters applied -> render whatever comes back.

  Two deliberate choices worth knowing before you change anything:

  1. The filter query string is built by `filterParams` from vz-fitment-core.js,
     the SAME function the vehicle finder uses. Do not hand-roll the params here.
     It encodes a rule that is easy to get wrong: fits_engine and
     fits_engine_code are mutually exclusive, because Shopify ANDs across filter
     keys and every "fits all engines" product would silently drop out if both
     were sent.

  2. Nothing is rendered until products actually come back. An empty strip is
     worse than no strip — it reads as a broken page.

  ---------------------------------------------------------------------------
  DIAGNOSTICS: set to false before launch.

  While true, the block always renders a visible panel on the product page
  showing what it found at every stage, instead of silently removing itself.
  That turns "nothing shows up" into a readable answer without opening a console.

  If you see NO panel at all, the module never loaded — check that
  assets/vz-taxonomy.js starts with `export default` and not `{`.
  --------------------------------------------------------------------------- */
import { readVehicle } from './vz-garage.js';
import { filterParams } from './vz-fitment-core.js?v=20261004a';

const DIAGNOSTIC = false;
const TIMEOUT_MS = 8000;

/* The taxonomy is loaded with a DYNAMIC import, not a static one, purely so a
   failure is survivable and visible. A static `import x from './vz-taxonomy.js'`
   that throws takes this whole file down with it — the custom element never
   registers and the page shows nothing at all, with no way to tell that from
   "the section isn't installed". That exact failure cost hours once already,
   when Shopify's CDN minifier mangled the taxonomy file. Keep it dynamic.

   The URL comes from Liquid (`asset_url`), NOT from a relative './' specifier.
   A relative import resolves without Shopify's `?v=` cache-busting hash, and
   the unversioned URL is served from a long-lived CDN cache — so after an edit
   the browser can keep loading a stale copy of this file indefinitely, while
   the versioned URL serves the correct one. Always import the versioned URL. */
let TAXONOMY = null;
let taxonomyError = null;

async function loadTaxonomy(url) {
  if (TAXONOMY || taxonomyError) return TAXONOMY;
  try {
    const module = await import(url || './vz-taxonomy.js');
    TAXONOMY = module.default;
    if (!TAXONOMY || !TAXONOMY.tagIndex) {
      taxonomyError = 'loaded but has no tagIndex — wrong file?';
      TAXONOMY = null;
    }
  } catch (error) {
    taxonomyError = error.message || String(error);
  }
  return TAXONOMY;
}

/* Tags no longer carry a cat:/sub: prefix, so a tag cannot be classified by
   shape — it has to be looked up. EXACT match on the lowercased string only:
   substring matching would make "Light" swallow "Brake Light". */
function lookup(tag) {
  if (!tag) return null;
  return TAXONOMY.tagIndex[String(tag).trim().toLowerCase()] || null;
}

function classify(tags) {
  const hits = (tags || []).map(lookup).filter(Boolean);
  return {
    sub: hits.find((h) => h.level === 2) || null,
    parent: hits.find((h) => h.level === 1) || null,
  };
}

/* Which categories should sit under this product. Sub-category first; fall back
   to the parent's own list, then to its siblings, so a part tagged only
   "Exhaust System" still gets something sensible. */
function targetsFor(tags) {
  const { sub, parent } = classify(tags);
  let names = [];

  if (sub) names = TAXONOMY.complements[sub.name] || [];
  if (!names.length && parent) {
    names = TAXONOMY.parentFallback[parent.name] || parent.subs || [];
  }

  const self = sub ? sub.name : null;
  const seen = new Set();
  return names
    .filter((n) => n !== self && !seen.has(n) && seen.add(n))
    .map((n) => TAXONOMY.tagIndex[n.toLowerCase()])
    .filter(Boolean);
}

function vehicleLabel(vehicle) {
  if (!vehicle) return '';
  return [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' ');
}

function buildUrl(target, vehicle, cfg) {
  const qs = new URLSearchParams();

  const params = vehicle ? filterParams(vehicle) : null;
  /* append, never set — a key legitimately repeats (one per engine code). */
  if (params) for (const [key, value] of params) qs.append(key, value);
  qs.append('section_id', cfg.gridSection);

  const base = String(cfg.root || '/').replace(/\/$/, '');
  return `${base}/collections/${encodeURIComponent(target.handle)}?${qs}`;
}

async function fetchGroup(target, vehicle, cfg) {
  const url = buildUrl(target, vehicle, cfg);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) return { target, url, status: response.status, items: [] };

    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    return {
      target,
      url,
      status: response.status,
      items: Array.from(parsed.querySelectorAll('[data-recs-item]')),
    };
  } catch (error) {
    /* A dead group is not a page error — drop it and let the others render. */
    return { target, url, status: String(error.name || error), items: [] };
  } finally {
    clearTimeout(timer);
  }
}

class VzRecs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    try {
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
    } catch (error) {
      this.#giveUp('could not read the section config JSON: ' + error.message);
      return;
    }

    /* Defer so this never competes with the main product image for bandwidth.

       Do NOT gate this on IntersectionObserver watching `this`. The element
       starts hidden, hidden means display:none, and an element with no box
       never intersects anything — the callback would never fire and the block
       would stay invisible on every product. Schedule on idle time instead. */
    const start = () => this.build();

    if (document.readyState === 'complete') {
      this.#whenIdle(start);
    } else {
      window.addEventListener('load', () => this.#whenIdle(start), { once: true });
    }
  }

  #whenIdle(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 1500 });
    } else {
      setTimeout(callback, 200);
    }
  }

  async build() {
    if (!(await loadTaxonomy(this.config.taxonomyUrl))) {
      this.#giveUp('taxonomy file failed to load — ' + taxonomyError);
      return;
    }

    const vehicle = readVehicle();

    /* Fetch EVERY candidate category, then keep the first maxGroups that came
       back with something. Slicing before the fetch means an empty category
       burns a slot and pushes a stocked one off the end — which on a thin
       catalogue is the difference between a strip and no strip at all. */
    const targets = targetsFor(this.config.tags);

    this.report = {
      tags: this.config.tags,
      vehicle,
      matched: classify(this.config.tags),
      targets,
      groups: [],
    };

    if (!targets.length) {
      this.#giveUp('no category in the taxonomy matched any of this product’s tags');
      return;
    }

    const groups = await Promise.all(targets.map((t) => fetchGroup(t, vehicle, this.config)));
    this.report.groups = groups;

    /* Dedupe across groups: a part that sits in two categories (Tie Rod is
       Steering AND Suspension) would otherwise appear twice on one page. */
    const seen = new Set([this.config.handle]);
    const take = (group, limit) =>
      group.items
        .filter((item) => {
          const handle = item.dataset.handle;
          return handle && !seen.has(handle) && seen.add(handle);
        })
        .slice(0, Math.max(0, limit));

    const rendered = [];
    const total = () => rendered.reduce((sum, g) => sum + g.items.length, 0);

    for (const group of groups) {
      if (rendered.length >= this.config.maxGroups) break;
      if (total() >= this.config.totalItems) break;

      const items = take(group, Math.min(this.config.perGroup, this.config.totalItems - total()));
      if (items.length) rendered.push({ target: group.target, items });
    }

    /* Top-up. The complement map is the interesting half, but on a thin
       catalogue it rarely fills the row — so pad from /collections/all with the
       SAME vehicle filters applied. Fitment is never relaxed to reach the
       target: showing a part that does not fit their car to fill a gap earns a
       return and a refund, which costs more than a short row. */
    const shortfall = this.config.totalItems - total();
    if (shortfall > 0) {
      const filler = await fetchGroup({ name: '__filler__', handle: 'all' }, vehicle, this.config);
      this.report.groups.push(filler);

      const items = take(filler, shortfall);
      if (items.length) rendered.push({ target: filler.target, items, isFiller: true });
    }

    if (!rendered.length) {
      this.#giveUp('every category returned zero products for this vehicle');
      return;
    }

    this.renderInto(rendered, vehicle);
    if (DIAGNOSTIC) this.#renderReport('rendered ' + rendered.length + ' group(s)');
  }

  /* In production: delete the block silently. In diagnostics: say why. */
  #giveUp(reason) {
    if (!DIAGNOSTIC) {
      this.remove();
      return;
    }
    this.hidden = false;
    this.#renderReport(reason);
  }

  #renderReport(conclusion) {
    const r = this.report || {};
    const rows = [];

    rows.push([
      'Module',
      TAXONOMY
        ? 'loaded — taxonomy has ' + Object.keys(TAXONOMY.tagIndex).length + ' categories'
        : 'loaded, but the TAXONOMY did not: ' + taxonomyError,
    ]);
    if (!TAXONOMY) {
      rows.push([
        'Taxonomy URL',
        '<a href="' + (this.config.taxonomyUrl || './vz-taxonomy.js') +
          '" target="_blank" rel="noopener">open it</a> — the LAST line must read ' +
          '“export default VZ_TAXONOMY;”. If it starts with “Minification failed”, ' +
          'that is the CDN serving a stale or unminifiable copy.',
      ]);
    }
    rows.push(['Product tags', (r.tags || (this.config && this.config.tags) || []).join(', ') || '(none)']);
    rows.push([
      'Matched category',
      r.matched
        ? [r.matched.sub && 'sub: ' + r.matched.sub.name, r.matched.parent && 'parent: ' + r.matched.parent.name]
            .filter(Boolean).join('  •  ') || '(no tag matched the taxonomy)'
        : '—',
    ]);
    rows.push([
      'Saved vehicle',
      r.vehicle ? vehicleLabel(r.vehicle) + ' — ' + JSON.stringify(r.vehicle) : 'garage is EMPTY (no filters applied)',
    ]);
    rows.push(['Categories tried', (r.targets || []).map((t) => t.handle).join(', ') || '(none)']);

    for (const g of r.groups || []) {
      rows.push([
        '↳ ' + g.target.handle,
        g.items.length + ' product(s), HTTP ' + g.status +
          '  —  <a href="' + g.url.replace(/&/g, '&amp;') + '" target="_blank" rel="noopener">open this request</a>',
      ]);
    }

    rows.push(['Result', conclusion]);

    const panel = document.createElement('div');
    panel.style.cssText =
      'margin:1rem auto;max-width:1100px;padding:1rem 1.25rem;border:2px dashed #c00;' +
      'border-radius:8px;font:13px/1.6 ui-monospace,SFMono-Regular,Menlo,monospace;' +
      'background:#fff8f8;color:#300;overflow-x:auto';
    panel.innerHTML =
      '<strong style="display:block;margin-bottom:.5rem;font-size:14px">' +
      'vz-recs diagnostics — set DIAGNOSTIC = false in vz-recs.js before launch</strong>' +
      rows
        .map(
          ([k, v]) =>
            '<div><span style="display:inline-block;min-width:150px;color:#900">' +
            k + '</span>' + v + '</div>'
        )
        .join('');

    const inner = this.querySelector('.vz-recs__inner') || this;
    inner.appendChild(panel);
  }

  renderInto(groups, vehicle) {
    const body = this.querySelector('[data-recs-body]');
    const heading = this.querySelector('[data-recs-heading]');
    const note = this.querySelector('[data-recs-note]');

    if (heading) {
      heading.textContent = vehicle
        ? this.strings.headingFits.replace('%VEHICLE%', vehicleLabel(vehicle))
        : this.strings.headingGeneric;
    }

    /* Without a saved car these are category matches, not fitment matches.
       Say so, and give them the one-click way to fix it. */
    if (note) {
      if (vehicle) {
        note.hidden = true;
      } else {
        note.hidden = false;
        note.querySelector('[data-recs-note-text]').textContent = this.strings.noVehicle;
        note.querySelector('[data-vz-garage-open]').textContent = this.strings.chooseVehicle;
      }
    }

    body.textContent = '';
    for (const group of groups) {
      const section = document.createElement('div');
      section.className = 'vz-recs__group';

      const title = document.createElement('h3');
      title.className = 'vz-recs__group-title';

      if (group.isFiller) {
        /* Labelled separately on purpose: these are not related to the part
           being viewed, they just fit the same car. Presenting them as if the
           complement logic chose them would be a small lie that erodes trust
           in the whole strip. */
        section.classList.add('vz-recs__group--filler');
        title.textContent = vehicle
          ? this.strings.moreFits.replace('%VEHICLE%', vehicleLabel(vehicle))
          : this.strings.moreGeneric;
      } else {
        title.textContent = group.target.name;
      }

      const list = document.createElement('ul');
      list.className = 'vz-recs__list';
      for (const item of group.items) list.appendChild(item);

      section.append(title, list);
      body.appendChild(section);
    }

    this.hidden = false;
  }
}

if (!customElements.get('vz-recs')) {
  customElements.define('vz-recs', VzRecs);
}